import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { forsideData, siteBranding } from '../data';
import { getResizedImageUrl, getImageSrcSet } from '../lib/utils';
import SEO from '../components/SEO';

const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SportsActivityLocation",
      "@id": "https://moonyoga-fredericia.dk/#localbusiness",
      "name": "Moon Yoga Fredericia",
      "image": "https://assets.auxodev.dk/assets/8bfce4c8-263d-41f5-85ff-0c7b7ca56872/moon-20yoga-20logo-300px.png",
      "url": "https://moonyoga-fredericia.dk",
      "telephone": "+4522393836",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Dalegade 61B",
        "addressLocality": "Fredericia",
        "postalCode": "7000",
        "addressCountry": "DK"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "55.5654",
        "longitude": "9.7608"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday"
          ],
          "opens": "06:00",
          "closes": "22:00"
        }
      ],
      "sameAs": [
        siteBranding.facebook,
        siteBranding.instagram
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://moonyoga-fredericia.dk/#website",
      "url": "https://moonyoga-fredericia.dk",
      "name": "Moon Yoga Fredericia",
      "publisher": {
        "@id": "https://moonyoga-fredericia.dk/#localbusiness"
      }
    }
  ]
};

export default function Moonyoga() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const listener = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  // Automatic slideshow
  useEffect(() => {
    if (prefersReducedMotion) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % forsideData.slideshow.length);
    }, 6000); // Slow, calm 6s interval

    return () => clearInterval(interval);
  }, [prefersReducedMotion]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % forsideData.slideshow.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + forsideData.slideshow.length) % forsideData.slideshow.length);
  };

  return (
    <div className="space-y-20 sm:space-y-24 pb-20">
      <SEO
        title="Moon Yoga Fredericia | Nærværende yoga, meditation & ro"
        description="Mærk roen hos Moon Yoga Fredericia. Vi tilbyder blid yoga, meditation, måneceremonier og cupping-behandlinger i trygge rammer. Se vores hold og medlemskaber her."
        path="/"
        schema={homepageSchema}
      />
      
      {/* 1. Herosektion med slideshow */}
      <section className="relative z-0 w-full h-[70vh] sm:h-[80vh] min-h-[500px] overflow-hidden bg-brand-text" aria-label="Velkommen slideshow">
        {/* Slides */}
        {forsideData.slideshow.map((slide, index) => (
          <div
            key={slide.url}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img
              src={getResizedImageUrl(slide.url, "width=1600,quality=80,format=auto")}
              srcSet={getImageSrcSet(slide.url, [400, 800, 1200, 1600, 2000])}
              sizes="100vw"
              alt={slide.alt}
              className="w-full h-full object-cover object-center"
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : undefined}
              width="1600"
              height="900"
            />
            {/* Dark warm overlay for readability - toned down for a brighter, more natural look */}
            <div className="absolute inset-0 bg-black/20" />
          </div>
        ))}

        {/* Slideshow Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-primary cursor-pointer"
          aria-label="Forrige billede"
        >
          <i className="fa-light fa-chevron-left text-xl" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 h-12 w-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-primary cursor-pointer"
          aria-label="Næste billede"
        >
          <i className="fa-light fa-chevron-right text-xl" />
        </button>

        {/* Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2.5">
          {forsideData.slideshow.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 rounded-full transition-all duration-500 cursor-pointer ${
                index === currentSlide ? 'w-8 bg-brand-primary' : 'w-2.5 bg-white/50 hover:bg-white'
              }`}
              aria-label={`Gå til slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 text-center text-white pointer-events-none">
          <div className="max-w-3xl space-y-6 pointer-events-auto">
            <span className="text-xs sm:text-sm tracking-[0.3em] uppercase text-brand-bg/90 font-medium block">
              {forsideData.hero.overtitle}
            </span>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-wide leading-tight">
              {forsideData.hero.title}
            </h1>
            
            {/* Fallback description for immediate professional look */}
            <p className="text-base sm:text-lg text-brand-bg/90 leading-relaxed max-w-2xl mx-auto">
              {forsideData.hero.fallbackIntro}
            </p>

            <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/holdplan"
                className="px-8 py-3.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white font-medium tracking-wide shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Se holdplan
              </Link>
              <Link
                to="/priser-vilkaar"
                className="px-8 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium tracking-wide border border-white/20 backdrop-blur-sm transition-all duration-300"
              >
                Se priser & vilkår
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Kort introduktion til Moon Yoga */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
        <div className="inline-block h-1 w-16 bg-brand-primary/30 rounded-full" />
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary font-semibold tracking-wide">
          {forsideData.intro.title}
        </h2>
        <div className="space-y-6 text-brand-text/90 text-base sm:text-lg leading-relaxed text-left sm:text-center">
          {forsideData.intro.paragraphs.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </section>

      {/* 3. Yoga, Styrke og Mindset */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-labelledby="values-heading">
        <h2 id="values-heading" className="sr-only">Vores værdier - Yoga, Styrke og Mindset</h2>
        <div className="bg-brand-surface border border-brand-primary/10 rounded-3xl p-8 sm:p-12 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {forsideData.threePoints.map((point) => (
              <div key={point.id} className="flex flex-col items-center text-center space-y-3">
                <div className="h-14 w-14 rounded-full bg-brand-primary/5 text-brand-primary flex items-center justify-center mb-1">
                  <i className={`${point.icon || 'fa-light fa-spa'} text-2xl`} aria-hidden="true" />
                </div>
                <h3 className="font-serif text-2xl font-semibold tracking-wide text-brand-primary">
                  {point.title}
                </h3>
                <p className="text-base sm:text-lg text-brand-text font-serif italic">
                  {point.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Udvalgte tilbud eller genveje */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="text-center space-y-2">
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
            Udforsk vores univers
          </h2>
          <p className="text-sm sm:text-base text-brand-muted">
            Find vej til holdplanen, priserne eller mød vores instruktører.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link
            to="/holdplan"
            className="group block p-6 rounded-2xl bg-brand-surface border border-brand-primary/5 hover:border-brand-primary/20 shadow-sm hover:shadow-md transition-all duration-300 text-center space-y-3"
          >
            <div className="inline-flex h-12 w-12 rounded-full bg-brand-primary/5 text-brand-primary items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <i className="fa-light fa-calendar-days text-xl" />
            </div>
            <h3 className="font-serif text-xl font-semibold text-brand-text group-hover:text-brand-primary transition-colors duration-300">
              Holdplan
            </h3>
            <p className="text-xs text-brand-muted">
              Se vores ugentlige klasser og book din plads nemt via YOGO.
            </p>
          </Link>

          <Link
            to="/priser-vilkaar#behandlinger"
            className="group block p-6 rounded-2xl bg-brand-surface border border-brand-primary/5 hover:border-brand-primary/20 shadow-sm hover:shadow-md transition-all duration-300 text-center space-y-3"
          >
            <div className="inline-flex h-12 w-12 rounded-full bg-brand-primary/5 text-brand-primary items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <i className="fa-light fa-spa text-xl" />
            </div>
            <h3 className="font-serif text-xl font-semibold text-brand-text group-hover:text-brand-primary transition-colors duration-300">
              Moon Treatments
            </h3>
            <p className="text-xs text-brand-muted">
              Udforsk vores cupping-behandlinger og klippekort.
            </p>
          </Link>

          <Link
            to="/moon-gatherings"
            className="group block p-6 rounded-2xl bg-brand-surface border border-brand-primary/5 hover:border-brand-primary/20 shadow-sm hover:shadow-md transition-all duration-300 text-center space-y-3"
          >
            <div className="inline-flex h-12 w-12 rounded-full bg-brand-primary/5 text-brand-primary items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <i className="fa-light fa-moon text-xl" />
            </div>
            <h3 className="font-serif text-xl font-semibold text-brand-text group-hover:text-brand-primary transition-colors duration-300">
              Moon Gatherings
            </h3>
            <p className="text-xs text-brand-muted">
              Deltag i vores magiske måneceremonier, events og fejringer.
            </p>
          </Link>

          <Link
            to="/instruktoerer"
            className="group block p-6 rounded-2xl bg-brand-surface border border-brand-primary/5 hover:border-brand-primary/20 shadow-sm hover:shadow-md transition-all duration-300 text-center space-y-3"
          >
            <div className="inline-flex h-12 w-12 rounded-full bg-brand-primary/5 text-brand-primary items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <i className="fa-light fa-users text-xl" />
            </div>
            <h3 className="font-serif text-xl font-semibold text-brand-text group-hover:text-brand-primary transition-colors duration-300">
              Moon Team
            </h3>
            <p className="text-xs text-brand-muted">
              Mød Camilla og Jozee og læs mere om vores hjertevarme undervisning.
            </p>
          </Link>
        </div>
      </section>

      {/* 6. Kortsektion */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-2">
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
            Find vores hyggelige studie
          </h2>
          <p className="text-sm sm:text-base text-brand-muted">
            Vi holder til i hjertet af Fredericia på {siteBranding.address}.
          </p>
        </div>
        
        <div className="relative w-full h-[350px] sm:h-[450px] rounded-3xl overflow-hidden shadow-md border border-brand-primary/10 bg-brand-light-gold">
          {/* Real interactive OpenStreetMap iframe (fully responsive, natural colors) */}
          <iframe
            title="Kort over Moon Yoga Fredericia placering"
            src="https://www.openstreetmap.org/export/embed.html?bbox=9.7500%2C55.5600%2C9.7700%2C55.5700&amp;layer=mapnik&amp;marker=55.5654%2C9.7608"
            className="absolute inset-0 w-full h-full border-0"
            allowFullScreen=""
            loading="lazy"
          />
          
          {/* Floating Address Card */}
          <div className="absolute bottom-6 left-6 z-30 bg-white/95 backdrop-blur-md p-6 rounded-2xl shadow-lg border border-brand-primary/10 max-w-xs space-y-3 hidden sm:block">
            <h3 className="font-serif text-lg font-semibold text-brand-primary">Moon Yoga Fredericia</h3>
            <p className="text-xs text-brand-text/80 leading-relaxed">
              Dalegade 61B<br />
              7000 Fredericia
            </p>
            <a
              href="https://maps.google.com/?q=Dalegade+61B,+7000+Fredericia"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-semibold text-brand-primary hover:text-brand-primary-hover transition-colors duration-300"
            >
              <i className="fa-solid fa-diamond-turn-right" />
              Find vej på Google Maps
            </a>
          </div>
        </div>
        
        {/* Mobile-only Find Vej button */}
        <div className="text-center sm:hidden">
          <a
            href="https://maps.google.com/?q=Dalegade+61B,+7000+Fredericia"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-primary text-white text-sm font-semibold transition-all duration-300"
          >
            <i className="fa-solid fa-diamond-turn-right" />
            Find vej på Google Maps
          </a>
        </div>
      </section>

      {/* 7. Afsluttende CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
        <div className="inline-block h-1 w-16 bg-brand-primary/30 rounded-full" />
        <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
          Er du klar til at give dig selv en pause?
        </h2>
        <p className="text-sm sm:text-base text-brand-muted max-w-xl mx-auto">
          Tilmeld dig en af vores ugentlige klasser og mærk den beroligende effekt af nærværende yoga.
        </p>
        <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/holdplan"
            className="px-8 py-3.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white font-medium tracking-wide shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Se holdplan
          </Link>
          <Link
            to="/priser-vilkaar"
            className="px-8 py-3.5 rounded-full bg-brand-light-gold hover:bg-brand-primary/5 text-brand-primary font-medium tracking-wide border border-brand-primary/20 transition-all duration-300"
          >
            Se priser
          </Link>
        </div>
      </section>

    </div>
  );
}
